import { requestCards } from "../protocol/cards";
import {
  parseReplyStates,
  readReplyContent,
  supportRequestFieldsSchema,
  type CardBlock,
  type ReplyState,
  type SupportRequestCard,
} from "@go-novus/admin-api";
import type { VerificationPrompt } from "./verification";
import { widgetBusinessFetch } from "../protocol/business-fetch";
import { orderActionError, type OrderActionFailure } from "./order-failure";
import { requestOrderPage } from "../protocol/orders";
import { err } from "neverthrow";
import { differenceInMilliseconds, parseISO } from "date-fns";

import type {
  ReplyMessage,
  SupportRequestCommand,
  SupportRequestFields,
} from "@go-novus/admin-api";

import { requestSupportDraft } from "../protocol/support-requests";
import {
  mergeSupportCards,
  reconcileSupportEdits,
  rememberSupportUpdate,
  supportBlockCard,
  supportCardData,
} from "./support-requests";
import {
  buildMessageContext,
  readMessageContext,
  startMessageCartRead,
} from "./message-context";
import type { ResolvedWidgetConfig } from "../config";
import * as m from "../paraglide/messages.js";
import {
  requestEmailCode,
  emailCodeFailure,
  type EmailVerificationAction,
  readCustomerAuthStatus,
  selectPlaygroundCustomer,
  storefrontLoginUrl,
  type CustomerAuth,
} from "../protocol/customer-auth";
import { reportWidgetEvent, type WidgetEventName } from "../protocol/events";
import {
  readTokenTenantId,
  requestHandshake,
  readTokenClaims,
  type HandshakeResult,
  toHandshakeObservation,
} from "../protocol/handshake";
import {
  requestConversationHistory,
  requestPendingAnswer,
} from "../protocol/history";
import { connectSocket, type WidgetSocket } from "../protocol/socket";
import {
  createUid,
  getOrCreateUid,
  readStoredUid,
  UID_STORAGE_KEY,
} from "../protocol/uid";
import { verificationPrompt } from "./verification";
import {
  compareCard,
  productCards,
  sizeCard,
  sourceLabel,
  type ReplyAttachments,
} from "./reply-attachments";
import {
  conversationSnapshotSchema,
  conversationStorageKey,
  readConversationSnapshot,
  readWidgetStorage,
  type ConversationSnapshot,
} from "./snapshot";

/** 加购回执：形状由快照 schema 定，挂件消息里的 WidgetCartReceipt 与它一致。 */
type CartReceipt = NonNullable<
  ConversationSnapshot["messages"][number]["cartReceipt"]
>;

type ConversationMessage = ConversationSnapshot["messages"][number] & {
  blocks?: CardBlock[];
  blocksFailed?: boolean;
  verification?: VerificationPrompt;
  supportRequests?: SupportRequestCard[];
  supportOpening?: {
    pending: boolean;
    ref?: string;
    orderReference?: string;
    error?: string;
  };
};
export type ConversationState = Omit<
  ConversationSnapshot,
  "messages" | "retry"
> & {
  messages: ConversationMessage[];
  retry?: NonNullable<ConversationSnapshot["retry"]>;
  supportUpdates?: SupportRequestCard[];
  eventWaiting?: boolean;
  disabledBlocks?: string[];
  maxTaskVersion?: number;

  orderBusy?: boolean;
  orderFailures?: Record<string, OrderActionFailure>;
  busy: boolean;
  disabled: boolean;
  error?: string;
  progress?: string;
  /** 聊天窗收起时出的错；和出错文案一样不进快照。 */
  unreadError?: boolean;
  /** 当前设备是否持有服务端确认的顾客认证。 */
  auth: CustomerAuth;
  /** 登录/登出流程在途:按钮置灰防重入。 */
  authBusy: boolean;
  supportRequestsAvailable: boolean;
};

function readStorage():
  Pick<Storage, "getItem" | "setItem" | "removeItem"> | undefined {
  return readWidgetStorage();
}

/** 续期失败：再发一次永远不会好，所以要跟普通发送失败分开报。 */
class PlaygroundSessionExpired extends Error {}

export interface ConversationConfig extends ResolvedWidgetConfig {
  /** 握手下发的支持邮箱，求助请求的固定收件人。宿主不注入。 */
  supportEmail?: string;
  capabilities?: { support_requests: boolean };
}

export interface VoiceCredential {
  token: string;
  conversationId?: string;
  uid: string;
}

export interface VoiceQuestion {
  conversationId: string;
  messageId: string;
  content: string;
}

export type ConversationUIEvent =
  { kind: "handoff"; messageId: string } | { kind: "verified"; email?: string };

interface ConversationDeps {
  requestHandshake: typeof requestHandshake;
  requestPendingAnswer: typeof requestPendingAnswer;
  requestConversationHistory: typeof requestConversationHistory;
  connectSocket: typeof connectSocket;
  reportWidgetEvent: typeof reportWidgetEvent;
  readCustomerAuthStatus: typeof readCustomerAuthStatus;
  selectPlaygroundCustomer: typeof selectPlaygroundCustomer;
  requestEmailCode: typeof requestEmailCode;
  requestCards: typeof requestCards;
  /** 整页去店铺登录页;测试里替换掉,不真的离开页面。 */
  navigate: (url: string) => void;
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | undefined;
}

/** 会话状态、重试和持久化由一个入口拥有；面板只负责输入与展示。 */
export function createConversation(
  config: ConversationConfig,
  onChange: (state: ConversationState) => void,
  overrides: Partial<ConversationDeps> = {},
) {
  const verifiedNotices = new Set<string>();
  const uiListeners = new Set<(event: ConversationUIEvent) => void>();
  const notifyUI = (event: ConversationUIEvent) => {
    for (const listener of uiListeners) listener(event);
  };
  const notifyVerification = (messageId: string, email?: string) => {
    if (verifiedNotices.has(messageId)) return;
    verifiedNotices.add(messageId);
    notifyUI({ kind: "verified", email });
  };
  const businessFetch = widgetBusinessFetch(config.handshakeUrl);
  const deps: ConversationDeps = {
    requestHandshake,
    requestPendingAnswer,
    requestConversationHistory,
    connectSocket,
    reportWidgetEvent,
    readCustomerAuthStatus,
    selectPlaygroundCustomer,
    requestEmailCode,
    requestCards,
    navigate: (url) => {
      window.location.assign(url);
    },
    storage: readStorage(),
    ...overrides,
  };
  const key = conversationStorageKey(
    config.shopDomain,
    config.playgroundSession?.uid,
  );
  let uid = config.playgroundSession?.uid;
  const snapshot = readConversationSnapshot(deps.storage, key) ?? {
    open: false,
    draft: "",
    messages: [],
  };
  if (uid === undefined) {
    try {
      uid = readStoredUid();
    } catch {
      /* 禁止存储时等发送创建 uid。 */
    }
  }
  let state: ConversationState = {
    ...snapshot,
    messages: snapshot.messages.map((message) => ({
      ...message,
      supportNeedsOpen:
        message.role === "ai" &&
        message.supportNeedsOpen !== false &&
        readReplyContent(message.text).blocks.some(
          (block) => block.type === "transfer",
        )
          ? true
          : message.supportNeedsOpen,
    })),
    busy: false,
    disabled: false,
    auth: { store_login: "signed_out", email_code: "unavailable" },
    authBusy: false,
    supportRequestsAvailable:
      (config.playgroundSession?.capabilities ?? config.capabilities)
        ?.support_requests ?? false,
  };
  // PRD 2.5：聊天窗开着时换到另一件商品，会话里插一行系统提示。这时挂载入口还在建会话，
  // 不能走 update()：它会回调重绘，而重绘要用的会话对象还没建好。
  const pageProduct = config.productContext;
  if (
    snapshot.open &&
    snapshot.messages.length > 0 &&
    pageProduct &&
    pageProduct.title !== "" &&
    pageProduct.id !== snapshot.contextProductId
  ) {
    state = {
      ...state,
      contextProductId: pageProduct.id,
      messages: [
        ...state.messages,
        {
          id: crypto.randomUUID(),
          role: "system",
          text: pageProduct.title,
          textFormat: "plain",
        },
      ],
    };
    try {
      deps.storage?.setItem(
        key,
        JSON.stringify(conversationSnapshotSchema.parse(state)),
      );
    } catch {
      /* 隐私模式或配额耗尽不能影响店面。 */
    }
  }
  let socket: WidgetSocket | undefined;
  let connectingSocket: Promise<"ok" | "disabled" | "stale"> | undefined;
  // 后台连 Socket 用的凭据。令牌是短期的，过期后要换一份新的再连，所以不是常量。
  let playground = config.playgroundSession && {
    socketUrl: config.playgroundSession.socketUrl,
    token: config.playgroundSession.token,
    capabilities: config.playgroundSession.capabilities,
  };
  let disposed = false;
  const isDisposed = () => disposed;
  let finishReply: ((reply?: ReplyMessage) => void) | undefined;
  // 同一句重试复用 msg_id，后端也只当一条消息，所以只上报第一次收下的那次。
  const reportedMsgIds = new Set<string>();
  let restoreTimer: ReturnType<typeof setTimeout> | undefined;

  // 登录状态以服务端同步为准；生命周期触发器本身不构成授权。
  const AUTH_RECHECK_MIN_MS = 2_000;
  const AUTH_SYNC_INTERVAL_MS = 5 * 60 * 1000;
  const ANSWER_TIMEOUT_MS = 45_000;
  // 比 Gateway 的 90 秒多留 5 秒，让语音回答或超时说明先到达。
  const VOICE_ANSWER_TIMEOUT_MS = 95_000;
  const MERGE_WINDOW_MS = 30_000;
  const answeredQuestions = new Map<
    string,
    { reply: ReplyMessage; learned: number; receivedAt: number }
  >();
  const settledReplyIds = new Set<string>();
  const learnedByQuestion = new Map<string, number>();
  const voiceQuestionIds = new Set<string>();
  const voiceAnsweredQuestionIds = new Set<string>();
  const voiceAnswerReplyIds = new Map<string, string>();
  let learned = 0;
  type VoiceWait = {
    messageId: string;
    seq: number;
    timer: ReturnType<typeof setTimeout>;
  };
  type TextWait = {
    messageId?: string;
    seq?: number;
    answered?: boolean;
  };
  let voiceWait: VoiceWait | undefined;
  let textWait: TextWait | undefined;
  // 换页后闭包里的记录没了：待答问题的原话还原样躺在草稿里，就是超时放回去的那句。
  const restoredAsked = snapshot.pendingMessageId
    ? snapshot.messages.find(
        (message) =>
          message.role === "user" &&
          (message.serverMessageId ?? message.id) === snapshot.pendingMessageId,
      )
    : undefined;
  let timedOutDraft: { questionId: string; text: string } | undefined =
    snapshot.pendingMessageId &&
    snapshot.draft &&
    restoredAsked?.text === snapshot.draft
      ? { questionId: snapshot.pendingMessageId, text: snapshot.draft }
      : undefined;

  function rememberLearnedQuestion(messageId: string, seq: number) {
    learnedByQuestion.delete(messageId);
    learnedByQuestion.set(messageId, seq);
    while (learnedByQuestion.size > 20) {
      const oldest = learnedByQuestion.keys().next().value;
      if (oldest === undefined) break;
      learnedByQuestion.delete(oldest);
    }
  }
  // 记录已执行等待释放的回复 id；历史与恢复绘制不记录。
  function rememberSettledReply(replyId: string) {
    settledReplyIds.delete(replyId);
    settledReplyIds.add(replyId);
    while (settledReplyIds.size > 20) {
      const oldest = settledReplyIds.values().next().value;
      if (oldest === undefined) break;
      settledReplyIds.delete(oldest);
    }
  }

  const stillWaiting = () =>
    voiceWait !== undefined ||
    (textWait !== undefined && textWait.answered !== true);

  // message-center 会把 30 秒窗口合并到最新回答；回答 X 时结束不晚于 X 学到的等待。
  function answerUpTo(seq: number, textSeq = seq): boolean {
    let released = false;
    if (voiceWait !== undefined && voiceWait.seq <= seq) {
      clearTimeout(voiceWait.timer);
      voiceWait = undefined;
      released = true;
    }
    if (
      textWait?.seq !== undefined &&
      textWait.seq <= textSeq &&
      textWait.answered !== true
    ) {
      textWait.answered = true;
      released = true;
      finishReply?.();
    }
    return released;
  }

  function releaseWait(
    questionId: string,
    allowLearnedFallback = true,
    answeredMessageIds?: string[],
  ): Partial<ConversationState> {
    const covers = (id: string | undefined) =>
      id !== undefined &&
      (answeredMessageIds?.includes(id) ?? id === questionId);
    const voice = covers(voiceWait?.messageId) ? voiceWait : undefined;
    const text = covers(textWait?.messageId) ? textWait : undefined;
    const pending = covers(state.pendingMessageId);
    const retryMessage = state.retry
      ? state.messages.find((message) => message.id === state.retry?.msgId)
      : undefined;
    const retryAnswered = covers(retryMessage?.serverMessageId);
    const seq =
      voice?.seq ??
      text?.seq ??
      (allowLearnedFallback ? learnedByQuestion.get(questionId) : undefined);
    let released = false;
    if (answeredMessageIds === undefined) {
      released = seq === undefined ? false : answerUpTo(seq);
    } else {
      // 有明确覆盖范围时只按 id 释放，不能再按学到问题的顺序推断合并。
      if (voice !== undefined) {
        clearTimeout(voice.timer);
        voiceWait = undefined;
        released = true;
      }
      if (text !== undefined && text.answered !== true) {
        text.answered = true;
        released = true;
        finishReply?.();
      }
    }
    const retrySeq = retryMessage?.serverMessageId
      ? learnedByQuestion.get(retryMessage.serverMessageId)
      : undefined;
    const clearTimedOutDraft =
      timedOutDraft !== undefined &&
      covers(timedOutDraft.questionId) &&
      state.draft === timedOutDraft.text;
    if (
      !voice &&
      !text &&
      !pending &&
      !released &&
      !retryAnswered &&
      !clearTimedOutDraft
    )
      return {};
    const clearRetry =
      retryAnswered ||
      (answeredMessageIds === undefined &&
        seq !== undefined &&
        retrySeq !== undefined &&
        retrySeq <= seq);
    const pendingMessageId =
      textWait?.messageId !== undefined && textWait.answered !== true
        ? textWait.messageId
        : voiceWait?.messageId;
    if (covers(timedOutDraft?.questionId)) timedOutDraft = undefined;
    return {
      busy: stillWaiting(),
      ...(pending ? { pendingMessageId } : {}),
      ...(text ? { progress: undefined } : {}),
      ...(clearRetry ? { retry: undefined } : {}),
      ...(retryAnswered && state.retry?.text === state.draft
        ? { draft: "" }
        : {}),
      ...(clearTimedOutDraft ? { draft: "" } : {}),
      error: undefined,
    };
  }

  let lastAuthCheckAt = 0;
  /** 同一页面只保留一条登录同步请求。 */
  let authSyncInFlight = false;

  let renderGeneration = 0;
  let lastRenderAt = 0;
  let renderRetryAt = 0;
  let eventWaitTimer: ReturnType<typeof setTimeout> | undefined;
  let waitHistory = false;
  const orderEvents = new Map<string, string>();
  // 登录状态变化不作废会话；在途结果只核对设备 uid。
  const stale = (requestUid: string | undefined) =>
    isDisposed() || uid !== requestUid;

  function persistSnapshot() {
    try {
      deps.storage?.setItem(
        key,
        JSON.stringify(conversationSnapshotSchema.parse(state)),
      );
    } catch {
      /* 隐私模式或配额耗尽不能影响店面。 */
    }
  }

  /** 成功同步才记时间；不确定的登录信号也必须由服务端判定。 */
  async function refreshAuth(): Promise<void> {
    if (!uid) {
      try {
        uid = readStoredUid();
      } catch {
        /* 无存储时等待发送创建 uid。 */
      }
    }
    if (!uid || isDisposed() || authSyncInFlight) {
      return;
    }
    authSyncInFlight = true;
    const requestUid = uid;
    try {
      if (
        awaitingVerificationReply() &&
        (config.liquidCustomerId || state.auth.email_code === "verified")
      ) {
        if (
          (await ensureStorefrontSocket(uid)) === "ok" &&
          awaitingVerificationReply() &&
          !state.eventWaiting
        )
          beginEventWaiting(true);
      }
      const result = await deps.readCustomerAuthStatus(config.handshakeUrl, {
        uid: requestUid,
        conversationId: state.conversationId,
        liquidCustomerId: config.playgroundSession
          ? undefined
          : config.liquidCustomerId,
      });
      if (isDisposed() || uid !== requestUid) return;
      if (result.status === "ok") {
        lastAuthCheckAt = Date.now();
        const changed =
          result.auth.store_login !== state.auth.store_login ||
          result.auth.email_code !== state.auth.email_code;
        update({ auth: result.auth, authBusy: false });
        if (changed) void renderCards();
      } else if (state.authBusy) update({ authBusy: false });
    } finally {
      authSyncInFlight = false;
    }
  }

  /** 活页面的弱信号节流复查，结果未变时保留顾客视野和收发。 */
  function scheduleAuthRecheck() {
    if (Date.now() - lastRenderAt > 60_000 && Date.now() >= renderRetryAt)
      void renderCards();
    if (Date.now() - lastAuthCheckAt < AUTH_RECHECK_MIN_MS) return;
    void refreshAuth();
  }

  /** 页面恢复和连接通知只同步授权，不改变聊天记录。 */
  function recheckAuth() {
    void refreshAuth();
  }

  /**
   * 登录卡的动作:店面整页去店铺自己的登录页,登录后回到当前页由复查
   * 收口;Playground 没有店铺登录,商家填店内顾客的邮箱,以该顾客身份
   * 继续测查单。登录本身由服务端在会话上接手原任务。
   */
  async function verifyTask(messageId: string, email?: string): Promise<void> {
    const message = state.messages.find((message) => message.id === messageId);
    const verification = message?.verification;
    if (
      !message ||
      !state.conversationId ||
      disposed ||
      state.authBusy ||
      !message.blocks?.some((block) => block.actions.includes("store_login"))
    )
      return;
    if (
      verification &&
      (verification.lifecycle !== "active" ||
        parseISO(verification.expiresAt) <= new Date())
    ) {
      update({
        error: m.widget_verification_request_expired(),
        unreadError: collapsed(),
      });
      return;
    }
    if (!config.playgroundSession) {
      if (config.customerAccountsEnabled !== false && config.accountLoginUrl)
        deps.navigate(storefrontLoginUrl(config.accountLoginUrl));
      return;
    }
    if (!uid || email === undefined) return;
    const generation = uid;
    const requestUid = uid;
    update({ authBusy: true, error: undefined });
    const result = await deps.selectPlaygroundCustomer(config.handshakeUrl, {
      uid: requestUid,
      email,
      conversationId: state.conversationId,
    });
    if (stale(generation) || uid !== requestUid) return;
    if (result.status !== "ok" || result.auth.store_login !== "signed_in") {
      update({
        authBusy: false,
        error:
          result.status === "failed" && result.code === "customer_not_found"
            ? m.widget_playground_customer_not_found()
            : m.widget_error_signin_unavailable(),
      });
      return;
    }
    update({ auth: result.auth, authBusy: false });
    lastAuthCheckAt = Date.now();
    notifyVerification(messageId, email);
    await resumeVerification();
    await refreshAuth();
  }

  async function verifyTaskEmail(
    messageId: string,
    input: EmailVerificationAction,
  ) {
    if (!uid || !state.conversationId || disposed) return emailCodeFailure;
    const message = state.messages.find((message) => message.id === messageId);
    if (!message?.blocks?.some((block) => block.actions.includes("email_code")))
      return emailCodeFailure;
    if (
      message.verification &&
      (message.verification.lifecycle !== "active" ||
        parseISO(message.verification.expiresAt) <= new Date())
    )
      return emailCodeFailure;
    // 核验完成会立刻注入续接，先建立当前会话的连接。
    if ("code" in input) {
      try {
        if ((await ensureStorefrontSocket(uid)) !== "ok")
          return emailCodeFailure;
      } catch {
        return emailCodeFailure;
      }
    }
    const generation = uid;
    const requestUid = uid;
    const result = await deps.requestEmailCode(config.handshakeUrl, {
      ...input,
      uid: requestUid,
      conversationId: state.conversationId,
    });
    if (stale(generation) || uid !== requestUid) return emailCodeFailure;
    if (result.status === "verified") {
      notifyVerification(messageId, input.email || state.auth.login_hint_email);
      await resumeVerification();
      await refreshAuth();
    }
    return result;
  }

  const lastUserText = () =>
    [...state.messages].reverse().find((one) => one.role === "user")?.text ??
    "";
  // Playground 一直开着，快照里的 open 却从来不写。
  const collapsed = () => !state.open && !config.playgroundSession;

  // 活页面回到前台同步登录观测。
  const onPageVisible = () => {
    if (!document.hidden) scheduleAuthRecheck();
  };
  // bfcache 恢复时重新同步登录观测，聊天记录保持原样。
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      recheckAuth();
      return;
    }
    scheduleAuthRecheck();
  };
  // 跨标签只监听 uid；登录状态以服务端同步为准。
  const onStorageChange = (event: StorageEvent) => {
    if (uid === undefined) return;
    if (event.key === UID_STORAGE_KEY && !config.playgroundSession) {
      let next: string | undefined;
      try {
        next = readStoredUid();
      } catch {
        return;
      }
      if (next === uid) return;
      uid = next;
      void refreshAuth();
      return;
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", scheduleAuthRecheck);
    window.addEventListener("storage", onStorageChange);
    document.addEventListener("visibilitychange", onPageVisible);
    // 模块首次加载也同步；显式未登录信号同样需要送到服务端。
    // 延后到当前任务外:onChange 链路在 createConversation 返回前还没建好。
    setTimeout(() => {
      void refreshAuth().then(() => renderCards());
    }, 0);
  }

  async function resume(attempt = 0): Promise<void> {
    const { conversationId, pendingMessageId } = state;
    if (!conversationId || !pendingMessageId || isDisposed()) return;
    if (!uid) {
      try {
        uid = readStoredUid();
      } catch {
        /* 禁止存储时保留本页草稿。 */
      }
    }
    if (!uid) return;
    update({ busy: true });
    const generation = uid;
    const result = await deps.requestPendingAnswer(
      config.handshakeUrl,
      uid,
      conversationId,
      pendingMessageId,
      businessFetch,
    );
    // 只接收仍属于当前 uid、会话和待答消息的结果。
    if (
      stale(generation) ||
      state.conversationId !== conversationId ||
      state.pendingMessageId !== pendingMessageId
    )
      return;
    if (result.status === "ok" && result.answer) {
      completeAnswer(
        result.answer.message_id,
        result.answer,
        releaseWait(pendingMessageId),
      );
    } else if (result.status === "ok" && attempt < 14) {
      restoreTimer = setTimeout(() => {
        if (state.pendingMessageId === pendingMessageId)
          void resume(attempt + 1);
      }, 3000);
    } else {
      // 提示说「消息给你留在下面」，那就真得留下：发送时已经清空过，
      // 这里把买家最后那句放回去，不覆盖他这会儿正在打的字。
      update({
        busy: false,
        error: m.widget_error_answer_lost(),
        unreadError: collapsed(),
        draft: state.draft || lastUserText(),
      });
    }
  }

  function update(patch: Partial<ConversationState>) {
    if (disposed) return;
    if (patch.messages) {
      const sameConversation =
        !("conversationId" in patch) ||
        patch.conversationId === state.conversationId;
      patch.messages = mergeSupportCards(
        sameConversation ? state.messages : [],
        patch.messages,
        patch.supportUpdates ??
          (sameConversation ? state.supportUpdates : undefined),
      );
    }
    const oldMetadata = JSON.stringify(
      state.messages.map((message) => [
        message.id,
        message.replyStates,
        readReplyContent(message.text).blocks.some(
          (block) => block.type === "transfer",
        ),
      ]),
    );
    const switched =
      "conversationId" in patch &&
      patch.conversationId !== state.conversationId;
    const authChanged =
      patch.auth &&
      (patch.auth.store_login !== state.auth.store_login ||
        patch.auth.email_code !== state.auth.email_code);
    state = { ...state, ...patch };
    // 已知授权改变时先撤下订单投影；刷新失败只能保留同一授权下的旧内容。
    if (authChanged)
      state.messages = state.messages.map((message) => ({
        ...message,
        blocks: message.blocks?.map((block) =>
          (block.type === "order_list" || block.type === "order_detail") &&
          block.status === "ok"
            ? {
                type: block.type,
                state_index: block.state_index,
                status: "unavailable",
                actions: [],
              }
            : block,
        ),
      }));
    if (switched) {
      renderGeneration++;
      orderEvents.clear();
      waitHistory = false;
      finishEventWaiting();
    }
    if (patch.messages)
      state.messages = state.messages.map((message) => ({
        ...message,
        blocks: message.blocks?.map((block) => {
          if (block.type !== "support_request" || block.status !== "ok")
            return block;
          const current = message.supportRequests?.find(
            (card) => card.payload.request_id === block.data.request_id,
          );
          return current ? { ...block, data: supportCardData(current) } : block;
        }),
      }));
    state.maxTaskVersion = maximumTaskVersion();
    for (const card of state.messages.flatMap(
      (row) => row.supportRequests ?? [],
    )) {
      if (
        card.lifecycle !== "active" &&
        state.supportEdits?.[card.payload.request_id]
      ) {
        const edits = Object.fromEntries(
          Object.entries(state.supportEdits).filter(
            ([id]) => id !== card.payload.request_id,
          ),
        );
        state = { ...state, supportEdits: edits };
      }
    }
    persistSnapshot();
    onChange(state);
    if (
      patch.messages &&
      JSON.stringify(
        state.messages.map((message) => [
          message.id,
          message.replyStates,
          readReplyContent(message.text).blocks.some(
            (block) => block.type === "transfer",
          ),
        ]),
      ) !== oldMetadata
    )
      void renderCards();
  }

  function report(input: {
    event: WidgetEventName;
    conversationId: string;
    uid: string;
  }) {
    void deps.reportWidgetEvent({
      ...input,
      handshakeUrl: config.handshakeUrl,
      customerName: config.customerName,
    });
  }

  function completeAnswer(
    id: string,
    reply: ReplyAttachments & { content: string },
    settled: Partial<ConversationState> = {},
    live = true,
  ) {
    const hasTransfer = readReplyContent(reply.content).blocks.some(
      (block) => block.type === "transfer",
    );
    const products = productCards(reply, config.locale);
    const size = sizeCard(reply);
    const compare = compareCard(reply);
    const source = sourceLabel(reply);
    const replyStates = knownReplyStates(reply.reply_states);
    if (state.messages.some((message) => message.id === id)) {
      if (Object.keys(settled).length > 0) update(settled);
      return;
    }
    finishEventWaiting();
    update({
      messages: [
        ...state.messages.filter((message) => message.id !== id),
        {
          id,
          role: "ai",
          // 来源行暂时不画（GON-115），只有来源的回复算空，否则买家看到的是空气泡。
          text:
            reply.content ||
            (products || size || compare || replyStates.length || hasTransfer
              ? ""
              : m.widget_reply_empty()),
          ...(products ? { products } : {}),
          ...(size ? { sizeCard: size } : {}),
          ...(compare ? { compareCard: compare } : {}),
          ...(source ? { source } : {}),
          replyStates,
          supportNeedsOpen: hasTransfer ? true : undefined,
        },
      ],
      unreadReplyId: collapsed() ? id : undefined,
      ...settled,
    });
    if (live && hasTransfer) {
      notifyUI({ kind: "handoff", messageId: id });
      void openSupportRequest(id);
    }
  }

  /** 标成未送达：只有确定后端从头到尾没收下这条的调用方才用。 */
  const markUndelivered = (msgId: string) =>
    state.messages.map((message) =>
      message.id === msgId ? { ...message, failed: true } : message,
    );

  /** 发送与登录续接共用连接；同一 uid 的并发请求只建立一条 Socket。 */
  async function ensureStorefrontSocket(
    generation: string | undefined,
  ): Promise<"ok" | "disabled" | "stale"> {
    if (socket) return "ok";
    if (connectingSocket) return connectingSocket;
    const work = connectStorefrontSocket(generation);
    connectingSocket = work;
    try {
      return await work;
    } finally {
      if (connectingSocket === work) connectingSocket = undefined;
    }
  }

  async function readPlaygroundHandshake(
    forceRenew = false,
  ): Promise<HandshakeResult> {
    const session = config.playgroundSession;
    if (!session || !uid) throw new PlaygroundSessionExpired();
    const renewed =
      forceRenew ||
      state.auth.store_login === "signed_in" ||
      !playground ||
      readTokenTenantId(playground.token, uid) === null
        ? await session.renew()
        : playground;
    const claims = renewed && readTokenClaims(renewed.token, uid);
    if (!renewed || !claims) throw new PlaygroundSessionExpired();
    playground = renewed;
    return {
      status: "ok",
      ...renewed,
      ...claims,
      disabled: false,
      settings: session.settings,
      auth: state.auth,
    };
  }

  async function connectStorefrontSocket(
    generation: string | undefined,
  ): Promise<"ok" | "disabled" | "stale"> {
    const requestUid = uid;
    if (!requestUid || stale(generation)) return "stale";
    // 每次连接都以 POST 握手同步登录，令牌只标识会话 uid。
    const handshake = config.playgroundSession
      ? await readPlaygroundHandshake()
      : await deps.requestHandshake(
          config.handshakeUrl,
          requestUid,
          businessFetch,
          toHandshakeObservation(config),
        );
    if (stale(generation)) return "stale";
    if (handshake.status === "disabled") return "disabled";
    if (handshake.status !== "ok") throw new Error("Handshake unavailable");
    update({
      auth: handshake.auth,
      supportRequestsAvailable: handshake.capabilities.support_requests,
    });
    lastAuthCheckAt = Date.now();
    const connection: { socket?: WidgetSocket; invalidated: boolean } = {
      invalidated: false,
    };
    connection.socket = deps.connectSocket({
      ...handshake,
      uid: requestUid,
      onInvalidated: () => {
        connection.invalidated = true;
        if (socket === connection.socket) socket = undefined;
      },
    });
    if (connection.invalidated) throw new Error("Socket unavailable");
    socket = connection.socket;
    const refreshDrafts = async () => {
      await refreshAuth();
      if (stale(generation)) return;
      const ids = new Set(
        state.messages.flatMap(
          (row) =>
            row.supportRequests?.map((card) => card.payload.request_id) ?? [],
        ),
      );
      for (const requestId of ids)
        await operateSupportRequest({
          operation: "read",
          request_id: requestId,
        });
    };
    socket.onReconnect(() => {
      void refreshDrafts();
    });
    socket.onReply((frame) => {
      if (stale(generation) || frame.role !== "assistant") return;
      for (const questionId of frame.answered_message_ids ?? [
        frame.message_id,
      ]) {
        const previous = answeredQuestions.get(questionId);
        const recorded = {
          reply: frame,
          learned: previous?.learned ?? learned,
          receivedAt: previous?.receivedAt ?? Date.now(),
        };
        answeredQuestions.delete(questionId);
        answeredQuestions.set(questionId, recorded);
        if (voiceQuestionIds.has(questionId)) {
          voiceAnsweredQuestionIds.add(questionId);
          voiceAnswerReplyIds.set(questionId, frame.reply_id);
        }
      }
      while (answeredQuestions.size > 20) {
        const oldest = answeredQuestions.keys().next().value;
        if (oldest === undefined) break;
        answeredQuestions.delete(oldest);
      }
      if (frame.conversation_id === state.conversationId) {
        const settledBefore = settledReplyIds.has(frame.reply_id);
        rememberSettledReply(frame.reply_id);
        completeAnswer(
          frame.reply_id,
          frame,
          releaseWait(
            frame.message_id,
            !settledBefore,
            frame.answered_message_ids,
          ),
        );
      }
    });
    return "ok";
  }

  /**
   * 为 Gateway 取一份只用于本次通话的令牌。先连挂件自己的 Socket，确保
   * Gateway 后续转发的 AI 回复能回到同一个聊天；Playground 仍走续期路径。
   */
  async function getVoiceCredential(): Promise<VoiceCredential> {
    if (state.disabled) throw new Error(m.widget_voice_error());
    if (!uid) {
      try {
        uid = getOrCreateUid();
      } catch {
        uid = createUid();
      }
    }
    const requestUid = uid;
    if ((await ensureStorefrontSocket(requestUid)) !== "ok")
      throw new Error(m.widget_voice_error());
    if (isDisposed() || uid !== requestUid)
      throw new Error(m.widget_voice_error());

    const handshake = config.playgroundSession
      ? await readPlaygroundHandshake(true)
      : await deps.requestHandshake(
          config.handshakeUrl,
          requestUid,
          businessFetch,
          toHandshakeObservation(config),
        );
    if (isDisposed() || uid !== requestUid)
      throw new Error(m.widget_voice_error());
    if (handshake.status === "disabled")
      throw new Error(m.widget_voice_error());
    if (handshake.status !== "ok") throw new Error(m.widget_voice_error());
    update({
      auth: handshake.auth,
      supportRequestsAvailable: handshake.capabilities.support_requests,
    });
    lastAuthCheckAt = Date.now();
    return {
      token: handshake.token,
      conversationId: state.conversationId,
      uid: requestUid,
    };
  }

  /** Gateway 已获 message-center 确认的问题，直接作为本会话的买家消息落地。 */
  function addVoiceQuestion(
    question: VoiceQuestion,
    credentialUid: string,
  ): boolean {
    if (uid !== credentialUid || disposed) return false;
    const content = question.content.trim();
    if (!content || !question.messageId || !question.conversationId)
      return true;
    if (
      state.messages.some(
        (message) =>
          message.id === question.messageId ||
          message.serverMessageId === question.messageId,
      )
    ) {
      if (state.conversationId !== question.conversationId)
        update({ conversationId: question.conversationId });
      return true;
    }
    voiceQuestionIds.add(question.messageId);
    const recorded = answeredQuestions.get(question.messageId);
    answeredQuestions.delete(question.messageId);
    const answer =
      recorded?.reply.conversation_id === question.conversationId
        ? recorded.reply
        : undefined;
    const questionMessage: ConversationMessage = {
      id: question.messageId,
      serverMessageId: question.messageId,
      role: "user",
      text: content,
      textFormat: "plain",
    };
    const seq = ++learned;
    rememberLearnedQuestion(question.messageId, seq);
    const messages = [...state.messages];
    const ownAnswerIndex = answer
      ? messages.findIndex((message) => message.id === answer.reply_id)
      : -1;
    const canReorder =
      recorded !== undefined &&
      Date.now() - recorded.receivedAt <= MERGE_WINDOW_MS;
    if (answer !== undefined)
      voiceAnswerReplyIds.set(question.messageId, answer.reply_id);
    if (answer !== undefined && ownAnswerIndex >= 0 && canReorder) {
      const movableIds = new Set<string>();
      for (const [index, message] of messages.entries()) {
        if (index <= ownAnswerIndex || message.role !== "user") continue;
        const id = message.serverMessageId ?? message.id;
        const messageSeq = learnedByQuestion.get(id);
        if (
          !voiceQuestionIds.has(id) ||
          messageSeq === undefined ||
          messageSeq >= seq
        )
          continue;
        movableIds.add(message.id);
        const answerId = voiceAnsweredQuestionIds.has(id)
          ? voiceAnswerReplyIds.get(id)
          : undefined;
        if (answerId) {
          const answerRow = messages.find(
            (candidate, candidateIndex) =>
              candidateIndex > ownAnswerIndex && candidate.id === answerId,
          );
          if (answerRow) movableIds.add(answerRow.id);
        }
      }
      const movable = messages.filter((message) => movableIds.has(message.id));
      const withoutMovable = messages.filter(
        (message) => !movableIds.has(message.id),
      );
      const answerIndex = withoutMovable.findIndex(
        (message) => message.id === answer.reply_id,
      );
      withoutMovable.splice(answerIndex, 0, ...movable);
      const finalAnswerIndex = withoutMovable.findIndex(
        (message) => message.id === answer.reply_id,
      );
      withoutMovable.splice(finalAnswerIndex, 0, questionMessage);
      messages.splice(0, messages.length, ...withoutMovable);
    } else {
      messages.push(questionMessage);
    }
    if (answer !== undefined) voiceAnsweredQuestionIds.add(question.messageId);
    const clearTimedOutDraft =
      timedOutDraft?.questionId === question.messageId &&
      state.draft === timedOutDraft.text;
    if (timedOutDraft?.questionId === question.messageId)
      timedOutDraft = undefined;
    if (answer?.answered_message_ids !== undefined) {
      rememberSettledReply(answer.reply_id);
      update({
        conversationId: question.conversationId,
        contextProductId: config.productContext?.id,
        error: undefined,
        messages,
        ...(clearTimedOutDraft ? { draft: "" } : {}),
        ...releaseWait(question.messageId, false, answer.answered_message_ids),
      });
      completeAnswer(answer.reply_id, answer);
    } else if (answer !== undefined) {
      rememberSettledReply(answer.reply_id);
      const released = answerUpTo(seq, recorded?.learned ?? seq);
      const retryServerId = state.retry
        ? state.messages.find((message) => message.id === state.retry?.msgId)
            ?.serverMessageId
        : undefined;
      const retrySeq = retryServerId
        ? learnedByQuestion.get(retryServerId)
        : undefined;
      // 早到回答落地也要沿用 R7：只清理不晚于回答记录序号的文字重试。
      const clearRetry =
        recorded !== undefined &&
        retrySeq !== undefined &&
        retrySeq <= recorded.learned;
      const latestWait =
        textWait?.messageId !== undefined && textWait.answered !== true
          ? textWait.messageId
          : voiceWait?.messageId;
      update({
        conversationId: question.conversationId,
        contextProductId: config.productContext?.id,
        error: undefined,
        messages,
        ...(clearTimedOutDraft ? { draft: "" } : {}),
        ...(released
          ? {
              busy: stillWaiting(),
              pendingMessageId: latestWait,
            }
          : {}),
        ...(clearRetry
          ? {
              retry: undefined,
              ...(state.pendingMessageId === retryServerId
                ? { pendingMessageId: latestWait }
                : {}),
            }
          : {}),
      });
      completeAnswer(answer.reply_id, answer);
    } else {
      if (voiceWait !== undefined) clearTimeout(voiceWait.timer);
      const wait: VoiceWait = {
        messageId: question.messageId,
        seq,
        timer: setTimeout(() => {
          if (voiceWait !== wait) return;
          voiceWait = undefined;
          if (!state.draft)
            timedOutDraft = { questionId: question.messageId, text: content };
          update({
            busy: stillWaiting(),
            error: m.widget_error_no_answer(),
            unreadError: collapsed(),
            draft: state.draft || content,
          });
        }, VOICE_ANSWER_TIMEOUT_MS),
      };
      voiceWait = wait;
      update({
        conversationId: question.conversationId,
        contextProductId: config.productContext?.id,
        pendingMessageId: question.messageId,
        busy: true,
        error: undefined,
        messages,
      });
    }
    if (!reportedMsgIds.has(question.messageId)) {
      reportedMsgIds.add(question.messageId);
      report({
        event: "message_sent",
        conversationId: question.conversationId,
        uid: credentialUid,
      });
    }
    return true;
  }

  async function send(text: string, msgId?: string): Promise<void> {
    const content = text.trim();
    if (!content || state.busy || textWait || disposed || state.disabled)
      return;
    timedOutDraft = undefined;
    if (
      differenceInMilliseconds(Date.now(), lastAuthCheckAt) >
      AUTH_SYNC_INTERVAL_MS
    )
      void refreshAuth();
    const product = config.productContext;
    // 气泡上的 Retry 指名是哪条，就认哪条——同文的未送达气泡可能有好几条，
    // 按原文认会重发错的那条。输入框那边没有 id，只能按 retry 槽的原文认；
    // 发一句别的——哪怕跟某条失败消息一字不差——都是新消息新气泡。
    const previous = state.retry;
    const isRetry =
      previous !== undefined &&
      (msgId === undefined
        ? previous.text === content
        : msgId === previous.msgId);
    const retry = isRetry
      ? previous
      : { text: content, msgId: msgId ?? crypto.randomUUID() };
    const pending: TextWait = {};
    textWait = pending;
    const reusesMessageId = state.messages.some(
      (message) => message.id === retry.msgId,
    );
    update({
      busy: true,
      error: undefined,
      progress: undefined,
      contextProductId: product?.id,
      // 只收掉正在发出去的那一份草稿：从气泡上点重试时，输入框里可能是买家
      // 这会儿打的下一句，不能跟着一起清掉。
      draft: state.draft.trim() === content ? "" : state.draft,
      quickReplies: undefined,
      retry,
      // 重发同一句要留着 pendingMessageId：上次可能已拿到过 ack，这条在后端
      // 手里、答案还可能在生成，resume() 指望着它；换一句发才清。
      pendingMessageId: isRetry ? state.pendingMessageId : undefined,
      messages: reusesMessageId
        ? state.messages.map((message) =>
            // 再发一次先当正常气泡画；这轮也没收下才重新标未送达。
            message.id === retry.msgId
              ? { ...message, failed: undefined }
              : message,
          )
        : [...state.messages, { id: retry.msgId, role: "user", text: content }],
    });
    let unsubscribe: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let answerTimedOut = false;
    // finishReply 是全会话共享槽:本条的 resolver 单独抓住,收尾时只在
    // 槽位还属于这条的时候才清，免得把另一条发送的 resolver 提前放掉。
    let settle: ((reply?: ReplyMessage) => void) | undefined;
    // uid 在首次发送时创建，之后每个落点核对它是否仍当前。
    let generation = uid;
    try {
      if (!uid) {
        try {
          uid = getOrCreateUid();
        } catch {
          uid = createUid();
        }
      }
      generation = uid;
      // 新消息的购物车读取与握手/连接并行；真实空车带空车对象，失败或超时省略 cart。
      // 复用已有气泡的 msg_id 时不读车、不带 cart，包括较早失败气泡的重发。
      // 只在店面入口启用：Playground 会话即使误传店面路径也不读买家购物车。
      const cartRead = reusesMessageId
        ? undefined
        : startMessageCartRead(config);
      const session = config.playgroundSession;
      if (session !== undefined && playground !== undefined) {
        // 后台的令牌已经签好了，也没有店面开关这一说——挂件还没上线时
        // 商家照样要能试聊。
        let tenantId = readTokenTenantId(playground.token, uid);
        if (tenantId === null) {
          // 令牌过期：连不上也重连不好，先换一份新的。宿主为同一个 uid 重签，
          // 所以页面上之前的问答不丢。
          socket?.disconnect();
          socket = undefined;
          const renewed = await session.renew();
          if (isDisposed()) return;
          tenantId = renewed && readTokenTenantId(renewed.token, uid);
          if (renewed === null || tenantId === null)
            throw new PlaygroundSessionExpired();
          playground = renewed;
        }
        if (!socket) {
          const connected = await ensureStorefrontSocket(generation);
          if (connected !== "ok") return;
        }
      } else if (!socket) {
        const connected = await ensureStorefrontSocket(generation);
        if (connected !== "ok") {
          if (connected === "disabled")
            update({ disabled: true, messages: markUndelivered(retry.msgId) });
          return;
        }
      }
      if (!socket) return;
      const earlyReplies: ReplyMessage[] = [];
      const terminal = new Promise<ReplyMessage | undefined>((resolve) => {
        settle = resolve;
        finishReply = resolve;
        timer = setTimeout(() => {
          resolve(undefined);
        }, ANSWER_TIMEOUT_MS);
      });
      function receive(frame: ReplyMessage) {
        // 设备 uid 已变时，旧连接的迟到推送丢弃。
        if (stale(generation)) return;
        if (!pending.messageId) {
          earlyReplies.push(frame);
          return;
        }
        if (
          !(frame.role === "assistant" &&
          frame.answered_message_ids !== undefined
            ? frame.answered_message_ids.includes(pending.messageId)
            : frame.message_id === pending.messageId) ||
          frame.conversation_id !== state.conversationId
        )
          return;
        if (frame.role === "assistant") finishReply?.(frame);
        else update({ progress: frame.content });
      }
      unsubscribe = socket.onReply(receive);
      // 发送瞬间的有效购物车才进帧；读取异常时当没有购物车继续发。
      const cart = cartRead ? await cartRead : undefined;
      if (stale(generation)) return;
      const result = await socket.sendMessage({
        content,
        msgId: retry.msgId,
        conversationId: state.conversationId,
        context: buildMessageContext(config, product, cart),
      });
      if (stale(generation)) return;
      if (result.status !== "acked" || !result.ack.ok)
        throw new Error("Message rejected");
      pending.messageId = result.ack.message_id;
      const knownSeq = learnedByQuestion.get(result.ack.message_id);
      const alreadyAcked =
        knownSeq !== undefined ||
        state.messages.some(
          (message) =>
            message.id === retry.msgId && message.serverMessageId !== undefined,
        );
      pending.seq = alreadyAcked ? (knownSeq ?? 0) : ++learned;
      rememberLearnedQuestion(result.ack.message_id, pending.seq);
      update({
        conversationId: result.ack.conversation_id,
        pendingMessageId: result.ack.message_id,
        // 历史可能比 ack 先到；以 ack 的精确关联去重，不能按相同正文合并顾客消息。
        messages: state.messages
          .filter(
            (message) =>
              message.id !== result.ack.message_id ||
              message.id === retry.msgId,
          )
          .map((message) =>
            message.id === retry.msgId
              ? { ...message, serverMessageId: result.ack.message_id }
              : message,
          ),
      });
      // 后端收下才算「买家发了一条消息」。
      if (!reportedMsgIds.has(retry.msgId)) {
        reportedMsgIds.add(retry.msgId);
        report({
          event: "message_sent",
          conversationId: result.ack.conversation_id,
          uid,
        });
      }
      earlyReplies.forEach(receive);
      const answer = await terminal;
      if (stale(generation)) return;
      if (!answer) {
        if (pending.answered) {
          // 已 ack 的文字已随回答收尾，安静结束，不再报错。
          update({
            retry: undefined,
            ...(state.pendingMessageId === pending.messageId
              ? { pendingMessageId: undefined }
              : {}),
          });
          return;
        }
        answerTimedOut = true;
        throw new Error("Answer timed out");
      }
      completeAnswer(
        answer.reply_id,
        answer,
        releaseWait(answer.message_id, true, answer.answered_message_ids),
      );
    } catch (error) {
      // 这条气泡从没拿到服务端 ack（没有 serverMessageId）就是后端没收下：标成未送达（GON-46），
      // 重试就在气泡上，输入框不再放回原文。拿到过 ack 的只是没等到答案，气泡不动、
      // 没有重试入口，才把这句放回输入框好让买家直接重发；他可能已经在打下一句，别覆盖。
      if (!stale(generation)) {
        const undelivered = !state.messages.some(
          (message) =>
            message.id === retry.msgId && message.serverMessageId !== undefined,
        );
        const draftWasEmpty = state.draft === "";
        update({
          error:
            error instanceof PlaygroundSessionExpired
              ? m.widget_error_playground_expired()
              : undelivered
                ? m.widget_error_chat_unavailable()
                : m.widget_error_no_answer(),
          unreadError: collapsed(),
          ...(undelivered
            ? { messages: markUndelivered(retry.msgId) }
            : { draft: state.draft || content }),
        });
        if (
          !undelivered &&
          answerTimedOut &&
          draftWasEmpty &&
          pending.messageId
        )
          timedOutDraft = { questionId: pending.messageId, text: content };
        if (!answerTimedOut && voiceWait === undefined) {
          socket?.disconnect();
          socket = undefined;
        }
      }
    } finally {
      clearTimeout(timer);
      unsubscribe?.();
      settle?.();
      if (finishReply === settle) finishReply = undefined;
      if (textWait === pending) textWait = undefined;
      // 只为当前 uid 收尾。
      if (!stale(generation))
        update({ busy: stillWaiting(), progress: undefined });
    }
  }

  const blockKey = (messageId: string, index: number) =>
    `${messageId}:${String(index)}`;
  function knownReplyStates(input: unknown): ReplyState[] {
    return parseReplyStates(input).filter(
      (state) =>
        state.type === "order_check_suggested" ||
        ([
          "verification_required",
          "order_list",
          "order_list_empty",
          "order_detail",
        ].includes(state.type) &&
          !!state.ref &&
          !!state.task_version),
    );
  }
  function maximumTaskVersion() {
    return Math.max(
      0,
      ...state.messages.flatMap((message) =>
        (message.replyStates ?? []).map((value) => value.task_version ?? 0),
      ),
    );
  }
  async function renderCards() {
    if (disposed || !uid || !state.conversationId) return;
    const conversationId = state.conversationId;
    const generation = ++renderGeneration;
    const requestUid = uid;
    const messages = state.messages.filter(
      (message) =>
        message.role === "ai" &&
        ((message.replyStates?.length ?? 0) > 0 ||
          readReplyContent(message.text).blocks.some(
            (block) => block.type === "transfer",
          )),
    );
    if (!messages.length) return;
    lastRenderAt = Date.now();
    for (let offset = 0; offset < messages.length; offset += 20) {
      const batch = messages.slice(offset, offset + 20);
      if (batch.some((message) => message.blocksFailed)) {
        update({
          messages: state.messages.map((message) =>
            batch.some((item) => item.id === message.id)
              ? { ...message, blocksFailed: undefined }
              : message,
          ),
        });
      }
      const result = await deps.requestCards(
        config.handshakeUrl,
        {
          uid: requestUid,
          conversation_id: conversationId,
          liquid_customer_id: config.playgroundSession
            ? undefined
            : config.liquidCustomerId,
          messages: batch.map((message) => ({
            message_id: message.id,
            reply_states: message.replyStates ?? [],
          })),
        },
        businessFetch,
      );
      if (
        isDisposed() ||
        generation !== renderGeneration ||
        conversationId !== state.conversationId ||
        uid !== requestUid
      )
        return;
      if (result.isErr()) {
        update({
          messages: state.messages.map((message) =>
            batch.some((item) => item.id === message.id)
              ? {
                  ...message,
                  blocksFailed: true,
                  ...(readReplyContent(message.text).blocks.some(
                    (block) => block.type === "transfer",
                  ) &&
                  !message.supportRequests?.length &&
                  !message.supportOpening
                    ? {
                        supportOpening: { pending: false, error: result.error },
                      }
                    : {}),
                }
              : message,
          ),
        });
        continue;
      }
      renderRetryAt = result.value.retry_after
        ? Date.now() + result.value.retry_after * 1000
        : 0;
      update({ auth: result.value.auth });
      update({
        maxTaskVersion: maximumTaskVersion(),
        messages: state.messages.map((message) => {
          const rendered = result.value.messages.find(
            (item) => item.message_id === message.id,
          );
          if (!rendered) return message;
          // 只播报已在本页显示过的待验证块；历史首次读取没有状态迁移。
          if (
            message.blocks?.some(
              (block) =>
                block.type === "verification" &&
                block.status === "ok" &&
                block.data.status === "pending",
            ) &&
            rendered.blocks.some(
              (block) =>
                block.type === "verification" &&
                block.status === "ok" &&
                block.data.status === "verified",
            )
          ) {
            notifyVerification(message.id, result.value.auth.login_hint_email);
          }
          const keepOpened =
            message.supportOpening?.pending ||
            (!batch.find((row) => row.id === message.id)?.supportRequests
              ?.length &&
              !!message.supportRequests?.length);
          return {
            ...message,
            blocksFailed: undefined,
            supportNeedsOpen: rendered.blocks.some(
              (block) =>
                block.type === "support_request" && block.status === "ok",
            )
              ? undefined
              : message.supportNeedsOpen,
            blocks: keepOpened
              ? [
                  ...rendered.blocks.filter(
                    (block) => block.type !== "support_request",
                  ),
                  ...(message.blocks ?? []).filter(
                    (block) => block.type === "support_request",
                  ),
                ]
              : rendered.blocks,
            verification: verificationPrompt(
              rendered.blocks.find((block) => block.type === "verification"),
            ),
            supportRequests: keepOpened
              ? message.supportRequests
              : rendered.blocks.flatMap((block) =>
                  block.type === "support_request" &&
                  block.status === "ok" &&
                  block.data.phase !== "expired"
                    ? [supportBlockCard(block.data)]
                    : [],
                ),
          };
        }),
      });
      for (const message of batch) {
        const current = state.messages.find((row) => row.id === message.id);
        if (
          current &&
          current.supportNeedsOpen &&
          !current.blocks?.some(
            (block) =>
              block.type === "support_request" && block.status === "ok",
          ) &&
          !current.supportRequests?.length &&
          !current.supportOpening
        )
          void openSupportRequest(message.id);
      }
    }
  }
  async function recoverEventHistory() {
    if (!uid || !state.conversationId || disposed) return;
    const conversationId = state.conversationId;
    const generation = uid;
    const result = await deps.requestConversationHistory(
      config.handshakeUrl,
      uid,
      conversationId,
      businessFetch,
    );
    if (
      stale(generation) ||
      state.conversationId !== conversationId ||
      result.status !== "ok"
    )
      return;
    // 只补最后一条本地已知消息之后的回复；找不到锚点时不把旧历史当作新消息。
    const knownIds = new Set(
      state.messages.flatMap((message) => [
        message.id,
        ...(message.serverMessageId ? [message.serverMessageId] : []),
      ]),
    );
    let anchor = result.messages.length - 1;
    while (
      anchor >= 0 &&
      !knownIds.has(result.messages[anchor]?.message_id ?? "")
    )
      anchor--;
    if (anchor < 0) return;
    for (const message of result.messages.slice(anchor + 1)) {
      if (message.role === "assistant")
        completeAnswer(message.message_id, message, {}, false);
    }
    void renderCards();
  }
  function finishEventWaiting() {
    if (eventWaitTimer) clearTimeout(eventWaitTimer);
    eventWaitTimer = undefined;
    if (state.eventWaiting || state.disabledBlocks?.length)
      update({ eventWaiting: false, disabledBlocks: [] });
    if (waitHistory) {
      waitHistory = false;
      void recoverEventHistory();
    }
  }
  function beginEventWaiting(history = false) {
    if (eventWaitTimer) clearTimeout(eventWaitTimer);
    waitHistory ||= history;
    update({ eventWaiting: true });
    eventWaitTimer = setTimeout(finishEventWaiting, 60_000);
  }
  function awaitingVerificationReply() {
    const message = [...state.messages]
      .reverse()
      .find((message) => message.role === "ai");
    return (
      message?.verification?.lifecycle === "active" ||
      (message?.replyStates ?? []).some(
        (value) => value.type === "verification_required",
      )
    );
  }
  async function resumeVerification() {
    if (!awaitingVerificationReply()) {
      void renderCards();
      return;
    }
    if ((await ensureStorefrontSocket(uid)) !== "ok") return;
    if (awaitingVerificationReply() && !state.eventWaiting)
      beginEventWaiting(true);
    await renderCards();
  }
  async function orderAction(
    messageId: string,
    stateIndex: number,
    action: "select" | "more" | "not_received" | "check",
    orderRef?: string,
  ): Promise<void> {
    if (
      state.orderBusy ||
      state.eventWaiting ||
      state.busy ||
      !state.conversationId ||
      !uid
    )
      return;
    const message = state.messages.find((row) => row.id === messageId);
    const block = message?.blocks?.find(
      (value) =>
        value.state_index === stateIndex &&
        (action === "check"
          ? value.type === "order_check"
          : value.type.startsWith("order_")),
    );
    const replyState = message?.replyStates?.[stateIndex];
    const key = blockKey(messageId, stateIndex);
    const taskVersion = replyState?.task_version;
    if (
      !block ||
      block.status !== "ok" ||
      (block.type !== "order_list" &&
        taskVersion !== undefined &&
        taskVersion < maximumTaskVersion()) ||
      state.disabledBlocks?.includes(key)
    )
      return;
    const allowed =
      action === "select"
        ? "select_order"
        : action === "more"
          ? "load_more"
          : action === "check"
            ? "start_order_check"
            : "not_received";
    if (!block.actions.includes(allowed)) return;
    if (action !== "check" && action !== "more" && (!taskVersion || !orderRef))
      return;
    if (
      action === "select" &&
      (block.type !== "order_list" ||
        !block.data.orders.some((order) => order.order_ref === orderRef))
    )
      return;
    if (
      action === "not_received" &&
      (block.type !== "order_detail" || block.data.order_ref !== orderRef)
    )
      return;
    const conversationId = state.conversationId;
    const generation = uid;
    const sameConversation = () =>
      !stale(generation) && conversationId === state.conversationId;
    const current = () =>
      sameConversation() &&
      state.messages
        .find((row) => row.id === messageId)
        ?.blocks?.includes(block);
    update({
      orderBusy: true,
      orderFailures: Object.fromEntries(
        Object.entries(state.orderFailures ?? {}).filter(
          ([failureKey]) => failureKey !== key,
        ),
      ),
    });
    const fail = (error: string) => {
      if (sameConversation())
        update({
          disabledBlocks: state.disabledBlocks?.filter(
            (value) => value !== key,
          ),
          orderFailures: {
            ...state.orderFailures,
            [key]: {
              stateIndex,
              action,
              orderRef,
              error: orderActionError(error),
            },
          },
        });
    };
    try {
      if (action === "more") {
        if (
          block.type !== "order_list" ||
          !block.data.next_cursor ||
          !replyState?.ref
        )
          return;
        const result = await requestOrderPage(
          config.handshakeUrl,
          {
            uid,
            conversationId,
            liquidCustomerId: config.playgroundSession
              ? undefined
              : config.liquidCustomerId,
            ref: replyState.ref,
            cursor: block.data.next_cursor,
          },
          businessFetch,
        );
        if (!current()) return;
        if (result.isErr()) {
          fail(result.error);
          return;
        }
        const page = result.value;
        const replacement: CardBlock =
          page.status === "ok"
            ? {
                ...block,
                actions: [
                  "select_order",
                  ...(page.data.next_cursor ? ["load_more" as const] : []),
                ],
                data: {
                  ...page.data,
                  orders: [...block.data.orders, ...page.data.orders].filter(
                    (order, index, orders) =>
                      orders.findIndex(
                        (value) => value.order_ref === order.order_ref,
                      ) === index,
                  ),
                },
              }
            : {
                type: block.type,
                state_index: stateIndex,
                status: page.status,
                actions: [],
              };
        update({
          messages: state.messages.map((row) =>
            row.id === messageId
              ? {
                  ...row,
                  blocks: row.blocks?.map((value) =>
                    value === block ? replacement : value,
                  ),
                }
              : row,
          ),
        });
        return;
      }
      update({ disabledBlocks: [...(state.disabledBlocks ?? []), key] });
      if ((await ensureStorefrontSocket(generation)) !== "ok" || !current()) {
        fail("upstream_unavailable");
        return;
      }
      const eventKey = `${messageId}:${String(stateIndex)}:${action}:${orderRef ?? ""}`;
      const eventId = crypto.randomUUID();
      const assistantCount = state.messages.filter(
        (row) => row.role === "ai",
      ).length;
      const replyReceived = () =>
        state.messages.filter((row) => row.role === "ai").length >
        assistantCount;
      const frame: import("../protocol/socket").UiEventInput =
        action === "check"
          ? {
              conversation_id: conversationId,
              event_id: eventId,
              event: "start_order_check" as const,
              payload: {},
            }
          : action === "select"
            ? {
                conversation_id: conversationId,
                event_id: eventId,
                event: "order_selected" as const,
                task_version: taskVersion ?? 0,
                payload: { order_ref: orderRef ?? "" },
              }
            : {
                conversation_id: conversationId,
                event_id: eventId,
                event: "order_card_action" as const,
                task_version: taskVersion ?? 0,
                payload: {
                  order_ref: orderRef ?? "",
                  action: "not_received" as const,
                },
              };
      let ack = await socket?.sendUiEvent?.(frame);
      if (
        sameConversation() &&
        !replyReceived() &&
        !ack?.accepted &&
        (!ack?.error || ack.error === "ack_timeout")
      )
        ack = await socket?.sendUiEvent?.(frame);
      if (!sameConversation()) return;
      orderEvents.delete(eventKey);
      if (replyReceived()) {
        finishEventWaiting();
      } else if (ack?.accepted) {
        beginEventWaiting();
      } else {
        fail(ack?.error ?? "upstream_unavailable");
      }
    } catch {
      fail("upstream_unavailable");
    } finally {
      if (!stale(generation)) update({ orderBusy: false });
    }
  }

  async function operateSupportRequest(command: SupportRequestCommand) {
    const generation = uid;
    const conversationId = state.conversationId;
    const requestUid = uid ?? readStoredUid();
    if (!conversationId || !requestUid)
      return err({ error: "not_accessible" as const });
    const result = await requestSupportDraft(config.handshakeUrl, {
      uid: requestUid,
      conversationId,
      liquidCustomerId: config.playgroundSession
        ? undefined
        : config.liquidCustomerId,
      command,
    });
    if (
      stale(generation) ||
      state.conversationId !== conversationId ||
      uid !== requestUid
    )
      return err({ error: "not_accessible" as const });
    const card = result.isOk() ? result.value : result.error.card;
    if (card) {
      const edits = reconcileSupportEdits(
        state.supportEdits,
        card,
        command,
        result.isOk(),
        result.isErr() && result.error.error === "stale_version",
      );
      update({
        messages: mergeSupportCards(
          state.messages,
          state.messages.map((message) =>
            command.operation === "open" && message.id === command.message_id
              ? {
                  ...message,
                  supportNeedsOpen: undefined,
                  supportRequests: [card],
                  blocks: [
                    ...(message.blocks ?? []).filter(
                      (block) => block.type !== "support_request",
                    ),
                    {
                      type: "support_request",
                      status: "ok",
                      state_index: 0,
                      actions: [],
                      data: supportCardData(card),
                    },
                  ],
                }
              : message,
          ),
          [card],
        ),
        supportEdits: edits,
        supportUpdates: rememberSupportUpdate(state.supportUpdates, card),
      });
    }
    return result;
  }

  function editSupportRequest(
    requestId: string,
    version: number,
    fields: SupportRequestFields,
  ) {
    update({
      supportEdits: { ...state.supportEdits, [requestId]: { version, fields } },
    });
  }

  const openingRequests = new Map<
    string,
    Promise<Awaited<ReturnType<typeof operateSupportRequest>>>
  >();
  /** orderReference 是订单卡上的订单号，预填进采集卡的订单号字段（GON-295）；超出字段长度就不带。 */
  function openSupportRequest(
    messageId: string,
    ref?: string,
    orderReference?: string,
  ) {
    const key = `${uid ?? ""}:${state.conversationId ?? ""}:${messageId}`;
    const pending = openingRequests.get(key);
    if (pending) return pending;
    update({
      messages: state.messages.map((message) =>
        message.id === messageId
          ? {
              ...message,
              supportOpening: { pending: true, ref, orderReference },
            }
          : message,
      ),
    });
    const generation = uid;
    const conversationId = state.conversationId;
    const work = operateSupportRequest({
      operation: "open",
      message_id: messageId,
      ...(ref ? { ref } : {}),
      ...(orderReference &&
      supportRequestFieldsSchema.shape.order_reference.safeParse(orderReference)
        .success
        ? { order_reference: orderReference }
        : {}),
    })
      .then((result) => {
        if (!stale(generation) && conversationId === state.conversationId)
          update({
            messages: state.messages.map((message) =>
              message.id === messageId
                ? {
                    ...message,
                    supportNeedsOpen:
                      result.isErr() && result.error.error === "expired"
                        ? false
                        : message.supportNeedsOpen,
                    supportOpening: {
                      pending: false,
                      ref,
                      orderReference,
                      error: result.isErr() ? result.error.error : undefined,
                    },
                  }
                : message,
            ),
          });
        return result;
      })
      .finally(() => openingRequests.delete(key));
    openingRequests.set(key, work);
    return work;
  }

  return {
    subscribeUI: (listener: (event: ConversationUIEvent) => void) => {
      uiListeners.add(listener);
      return () => {
        uiListeners.delete(listener);
      };
    },
    orderAction,
    operateSupportRequest,
    editSupportRequest,
    openSupportRequest,
    getVoiceCredential,
    readMessageContext: () => readMessageContext(config),
    addVoiceQuestion,
    getState: () => state,
    resume,
    setOpen: (open: boolean) => {
      if (open && !state.open) void refreshAuth();
      update(
        open
          ? { open, unreadReplyId: undefined, unreadError: undefined }
          : { open },
      );
    },
    /** 买家关掉了回复提示。 */
    markRead: () => {
      update({ unreadReplyId: undefined, unreadError: undefined });
    },
    /**
     * 买家点了主动提示：打开聊天窗，提示作为 AI 的一句话进会话，规则的问题预填进输入框。
     * 这一步只记在本标签页的会话记录里，不发给 AI 后端、不建买家身份；买家回话才走 send()。
     */
    openWithPrompt: (prompt: string, question: string) => {
      update({
        open: true,
        draft: question,
        contextProductId: config.productContext?.id,
        messages: [
          ...state.messages,
          {
            id: crypto.randomUUID(),
            role: "ai",
            text: prompt,
            textFormat: "plain",
          },
        ],
        quickReplies: undefined,
      });
    },
    /**
     * 加购或改数量经 Shopify 确认后插一条回执。同一购物车行只留最新那条：旧回执上的件数已经过时，
     * 留着它的 Edit quantity 只会让买家对着旧数字改。只记在本标签页的会话记录里，不发给后端。
     */
    noteCart: (receipt: CartReceipt) => {
      const sameLine = (other: CartReceipt) =>
        receipt.lineKey !== undefined && other.lineKey !== undefined
          ? other.lineKey === receipt.lineKey
          : other.variantId === receipt.variantId;
      update({
        messages: [
          ...state.messages.filter(
            (message) =>
              message.cartReceipt === undefined ||
              !sameLine(message.cartReceipt),
          ),
          {
            id: crypto.randomUUID(),
            role: "system",
            text: receipt.title,
            textFormat: "plain",
            cartReceipt: receipt,
          },
        ],
      });
    },
    setDraft: (draft: string) => {
      timedOutDraft = undefined;
      update({ draft });
    },
    send,
    verifyTask,
    verifyTaskEmail,
    /** 供外部生命周期事件(换页恢复/可见性)触发的服务端复查。 */
    refreshAuth: scheduleAuthRecheck,
    dispose() {
      disposed = true;
      uiListeners.clear();
      if (typeof window !== "undefined") {
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("focus", scheduleAuthRecheck);
        window.removeEventListener("storage", onStorageChange);
        document.removeEventListener("visibilitychange", onPageVisible);
        if (eventWaitTimer) clearTimeout(eventWaitTimer);
      }
      clearTimeout(restoreTimer);
      if (voiceWait !== undefined) clearTimeout(voiceWait.timer);
      finishReply?.();
      socket?.disconnect();
    },
  };
}

export type Conversation = ReturnType<typeof createConversation>;
